import { DecimalPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Avenant } from '@app/core/models/datawell/avenant.model';
import { InvoiceAmendmentDetail } from '@app/core/models/invoices/invoice-amendment.model';
import { InvoiceAmountCalc } from '@app/core/models/invoices/invoice-amount-calc.model';
import { InvoiceDetailModel } from '@app/core/models/invoices/invoice-detail.model';
import { InvoiceParameters } from '@app/core/models/invoices/invoice-parameters.model';
import { AmendmentLibraryService } from '@app/core/services/manageInvoice/amendment-library.service';
import { BuildFormGroupsService } from '@app/core/services/manageInvoice/build-form-groups.service';
import { CommonLibraryService } from '@app/core/services/manageInvoice/common-library.service';
import { NotificationService } from '@app/core/services/notification.service';
import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-invoice-amendment',
  templateUrl: './invoice-amendment.component.html',
  styleUrls: ['./invoice-amendment.component.scss'],
})
export class InvoiceAmendmentComponent implements OnInit, AfterViewInit {
  unsubscribe$: Subject<boolean> = new Subject<boolean>();

  @Input() invoiceDetailModel: InvoiceDetailModel;
  @Input() invoiceDetailAmountCalc: InvoiceAmountCalc;
  @Input() invoiceDetailForm: UntypedFormGroup;

  @Input() invoiceParams: InvoiceParameters;
  @Input() isReadOnlyForm: boolean;
  @Input() isReadOnlyFormOrWaiting: boolean;
  @Input() submitted: boolean;

  @Input() dropDownDataVat = [];
  @Input() dropDownDataAvenant: Avenant[];

  @Input() editedDropdowns: string[];
  @Input() dropDownSettingsAvenant: any;

  @Output() onSetSumInvoiceTaxIncluded: EventEmitter<void> =
    new EventEmitter<void>();
  @Output() onAmendmentsChange: EventEmitter<UntypedFormArray> =
    new EventEmitter<UntypedFormArray>();
  @Output() onAmendmentsPatched: EventEmitter<UntypedFormArray> =
    new EventEmitter<UntypedFormArray>();
  @Output() onAddOrDeleteAmendments: EventEmitter<void> =
    new EventEmitter<void>();

  subcriptValueChanges: boolean = false;

  constructor(
    public commonLibrary: CommonLibraryService,
    public formInvoiceBuilder: BuildFormGroupsService,
    public amendmentLibrary: AmendmentLibraryService,
    private formBuilder: UntypedFormBuilder,
    private decimalPipe: DecimalPipe,
    private notificationService: NotificationService
  ) {}

  ngAfterViewInit(): void {
    this.patchAmendments();
  }

  ngOnInit(): void {
    this.setValueChangeSubscription();
  }

  setValueChangeSubscription() {
    this.subcriptValueChanges =
      this.InvoiceAmendments != undefined && this.InvoiceAmendments.length > 0;

    if (this.subcriptValueChanges) {
      this.InvoiceAmendments.valueChanges
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(() => {
          this.onAmendmentsChange.emit(this.InvoiceAmendments);
        });
    }
  }

  get InvoiceAmendments() {
    return this.invoiceDetailForm.controls['InvoiceAmendments'] as UntypedFormArray;
  }

  patchAmendments() {
    if (this.invoiceDetailModel.Amendments) {
      this.invoiceDetailModel.Amendments.forEach((x) => {
        this.InvoiceAmendments.push(this.addAmendmentFormGroup(x));
      });
      this.onAmendmentsPatched.emit(this.InvoiceAmendments);
    }
  }

  addAmendment() {
    const amendment: InvoiceAmendmentDetail = new InvoiceAmendmentDetail();
    amendment.CreatedAt = Date.now();
    this.invoiceDetailModel.Amendments.push(amendment);

    this.InvoiceAmendments.push(this.addAmendmentFormGroup(amendment));

    this.formInvoiceBuilder.markFormGroupTouched(this.invoiceDetailForm);
    this.setValueChangeSubscription();
    this.onAddOrDeleteAmendments.emit();
  }

  addAmendmentFormGroup(amendment: InvoiceAmendmentDetail): UntypedFormGroup {
    return this.formInvoiceBuilder.buildAmendmentFormGroup(
      amendment,
      this.invoiceParams,
      this.isReadOnlyForm
    );
  }

  deleteAmendment(index: number, CreatedAt: number) {
    const searchIndex: number = this.invoiceDetailModel.Amendments.findIndex(
      (x) => x.CreatedAt == CreatedAt
    );
    this.invoiceDetailModel.Amendments.splice(searchIndex, 1);
    this.InvoiceAmendments.removeAt(index);

    this.setSumAmendments();
    this.setValueChangeSubscription();
  }

  deleteAllAmendment() {
    this.invoiceDetailModel.Amendments = [];
    this.invoiceDetailForm.controls['InvoiceAmendments'] =
      this.formBuilder.array([]);

    this.setSumAmendments();
    this.setValueChangeSubscription();
  }

  onChangeAvenant(
    index: number,
    createdAt: number,
    selectedAvenant?: { numeroAvenant: string; label: string } | null
  ): void {
    this.editedDropdowns.push('AmendmentCode');

    var elementExist = this.invoiceDetailModel.Amendments.find(
      (c) =>
        c.Code == selectedAvenant?.numeroAvenant && c.CreatedAt != createdAt
    );

    if (elementExist) {
      this.setNullToItem(index);
      this.notificationService.error(
        'Attention',
        'Il est interdit de mettre 2 fois le même avenant sur une situation.'
      );
    } else {
      const avenantDatas = _.find(
        this.dropDownDataAvenant,
        (x) => x?.numeroAvenant === selectedAvenant?.numeroAvenant
      );

      var element = this.invoiceDetailModel.Amendments.find(
        (c) => c.CreatedAt == createdAt
      );
      element.Code = selectedAvenant?.numeroAvenant;
      element.Label = selectedAvenant?.label;

      var item = this.InvoiceAmendments.at(index) as UntypedFormGroup;
      item.controls.AmendmentCode.setValue(selectedAvenant?.numeroAvenant);
      item.controls.AmendmentLabel.setValue(
        selectedAvenant?.label === undefined ? [] : [selectedAvenant]
      );
      item.controls.AmendmentOriginalAmountTaxExcluded.setValue(
        this.decimalPipe.transform(avenantDatas?.montantHT, '1.2-2')
      );
      item.controls.AmendmentOriginalCumulTaxExcluded.setValue(
        this.decimalPipe.transform(avenantDatas?.avancementCumulHT, '1.2-2')
      );
      item.controls.AmendmentDescription.setValue(avenantDatas?.description1);
    }
  }

  setNullToItem(index: number) {
    var item = this.InvoiceAmendments.at(index) as UntypedFormGroup;
    item.controls.AmendmentCode.setValue(null);
    item.controls.AmendmentLabel.setValue([]);
  }

  setReadOnlyInformations(dataAvenant: Avenant[]) {
    this.dropDownDataAvenant = dataAvenant;

    if (this.dropDownDataAvenant && this.InvoiceAmendments) {
      this.InvoiceAmendments.controls.forEach((element, index) => {
        let currentForm = element as UntypedFormGroup;
        const code = currentForm.controls.AmendmentCode.value;
        const selectedAmendment = this.dropDownDataAvenant.find(
          (x) => x.numeroAvenant == code
        );
        currentForm.controls.AmendmentOriginalAmountTaxExcluded.setValue(
          this.decimalPipe.transform(selectedAmendment?.montantHT, '1.2-2')
        );
        currentForm.controls.AmendmentOriginalCumulTaxExcluded.setValue(
          this.decimalPipe.transform(
            selectedAmendment?.avancementCumulHT,
            '1.2-2'
          )
        );
        currentForm.controls.AmendmentDescription.setValue(
          selectedAmendment?.description1
        );
      });
      this.setSumAmendments();
    }
  }

  calculatedVatForAmendment(index: number, selectedFormGroup: UntypedFormGroup) {
    var element = this.invoiceDetailModel.Amendments.find(
      (c) => c.CreatedAt == selectedFormGroup.controls.CreatedAt.value
    );
    element.AmountTaxExcluded =
      selectedFormGroup.controls.AmendmentAmountTaxExcluded.value;

    const vatDatas = _.find(
      this.dropDownDataVat,
      (x) => x?.Code === this.invoiceDetailForm.controls.VatCode?.value[0]?.Code
    );

    this.updateExpenseAmountForAmendment(
      index,
      element.CreatedAt,
      vatDatas?.Value
    );
    this.setSumAmendments();
  }

  updateExpenseAmountForAllAmendment(tvavalue: number) {
    for (let i = 0; i < this.InvoiceAmendments.controls.length; i++) {
      const element = this.InvoiceAmendments.controls[i] as UntypedFormGroup;
      this.updateExpenseAmountForAmendment(
        i,
        element.controls.CreatedAt.value,
        tvavalue
      );
    }
    this.setSumAmendments();
  }

  updateExpenseAmountForAmendment(
    index: number,
    createdAt: number,
    tvavalue: number
  ) {
    this.amendmentLibrary.updateExpenseAmountForAmendment(
      this.invoiceDetailModel,
      this.InvoiceAmendments,
      index,
      createdAt,
      tvavalue
    );
  }

  setSumAmendments() {
    this.amendmentLibrary.setSumAmendments(
      this.invoiceDetailModel,
      this.invoiceDetailAmountCalc
    );
  }

  resetAmendments() {
    this.deleteAllAmendment();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
  }
}
